import { ReactNode } from 'react'
import cn from 'clsx'

// Components
import { Button, useMaxWidth, Image, Section } from '@tracklib/kit'

// Utils
import { CDN_ROOT } from '@constants'

import styles from './hero.css'

const HeroTitle = ({ children }: { children: JSX.Element }) => <h1 className={styles.title}>{children}</h1>

const HeroDescription = ({ children }: { children: JSX.Element }) => <p className={styles.description}>{children}</p>

const HeroButton = ({ children }: { children: JSX.Element }) => (
  <Button variant="white" isFake>
    {children}
  </Button>
)

type HeroProps = {
  image?: number | string
  loadingState?: boolean
  mobileImageOverride?: number | string
  video?: string
  videoMp4?: string
  mobileVideoOverride?: string
  mobileVideoOverrideMp4?: string
  children?: ReactNode
  marginBottom?: number
  marginBottomMobile?: number
  backgroundColor?: string
  large?: boolean
  small?: boolean
  full?: boolean
  gradient?: boolean
}

const Hero = ({
  loadingState,
  image,
  mobileImageOverride,
  video,
  videoMp4,
  mobileVideoOverride,
  mobileVideoOverrideMp4,
  children,
  marginBottom,
  marginBottomMobile,
  backgroundColor = '',
  large,
  small,
  full,
  gradient,
}: HeroProps): JSX.Element => {
  const isSmall = useMaxWidth()
  const ImageElement = (
    <Image
      image={mobileImageOverride && isSmall ? mobileImageOverride : image}
      absolute
      gradient={gradient}
      sizes="100vw"
      alt=""
      webp={false}
    />
  )
  const poster = Image.getSrc(image, 1920, 1080, 'jpg', false, CDN_ROOT)
  const bottomMargin = isSmall && marginBottomMobile ? marginBottomMobile : marginBottom

  const backgroundElement =
    video || videoMp4 ? (
      <video autoPlay muted loop playsInline poster={poster} className={styles.video}>
        {video && <source src={mobileVideoOverride && isSmall ? mobileVideoOverride : video} type="video/webm" />}
        {videoMp4 && <source src={mobileVideoOverrideMp4 && isSmall ? mobileVideoOverrideMp4 : videoMp4} type="video/mp4" />}
      </video>
    ) : (
      ImageElement
    )
  return (
    <div
      className={cn(styles.wrapper, large && styles.wrapperLarge, small && styles.wrapperSmall, full && styles.wrapperFull)}
      style={{ marginBottom: bottomMargin, backgroundColor }}
      datatesting="hero_wrapper"
    >
      {!loadingState && backgroundElement}
      <Section extraPad className={styles.section}>
        <div className={styles.content}>{children}</div>
      </Section>
    </div>
  )
}

Hero.Title = HeroTitle
Hero.Description = HeroDescription
Hero.Button = HeroButton

export default Hero
